import axios from 'axios';
import jwtDecode from 'jwt-decode';
import FuseUtils from '@fuse/FuseUtils';
import { bootInitialCustomIntercomLauncher, getAPIHost, getExpressAPIHost, getRegion, getId, getUserRegion, resolveId } from '../../../utils';
import Split from '../../split';
import _ from 'lodash';
import { signOut, getCurrentSession } from '@milkmoovement/common_cognito_package';

/**
 * Gets the region to use for API requests.
 * Prioritizes the cached region from sessionStorage (set by fetchRegionFromBackend),
 * falls back to URL-based region detection.
 * @returns {string} The region code
 */
const getRegionForAPI = () => {
    const cachedRegion = sessionStorage.getItem('mm.region');
    if (cachedRegion) {
        return cachedRegion;
    }
    return getUserRegion() || getRegion(window.location.hostname);
};

class JWTService extends FuseUtils.EventEmitter {
    _getUserHost = () => (Split.getBooleanTreatment('use_express_auth_endpoints') ? getExpressAPIHost() : getAPIHost());

    init() {
        // Ensure region is set before any split evaluation (e.g. _getUserHost)
        // to avoid cold-start routing to LoopBack when Express is intended.
        Split.setAttribute('region', getRegionForAPI());
        this.setInterceptors();
        this.handleAuthentication();
    }

    setInterceptors = () => {
        axios.interceptors.request.use(async (config) => {
            try {
                const session = await getCurrentSession();
                const jwt = session.getIdToken().getJwtToken();
                // eslint-disable-next-line no-param-reassign
                config.headers.Authorization = `Bearer ${jwt}`;
                // eslint-disable-next-line no-param-reassign
                config.headers.customer = getRegionForAPI();
                return config;
            } catch (e) {
                if (e.message === 'No current user') {
                    // This occurs post manual logout typically and we don't want to display a message
                    this.emit('onAutoLogout', null);
                } else {
                    this.emit('onAutoLogout', 'Token expired');
                }
                return {
                    ...config,
                    cancelToken: new axios.CancelToken((cancel) => cancel('User unauthenticated')),
                };
            }
        });
        axios.interceptors.response.use(
            (response) => response,
            (err) =>
                new Promise((resolve, reject) => {
                    if (err?.toJSON?.().message === 'Network Error' && !navigator.onLine) {
                        this.emit('onAutoLogout', 'Check your internet connection');
                    }
                    /* eslint-disable-next-line */
                    if (err.response && err.response.status === 401 && err.config && !err.config.__isRetryRequest) {
                        // Only auto-logout on 401 from auth-critical endpoints.
                        // Data endpoint 401s (e.g. /users?filter=...) during impersonation
                        // should not kill the entire session.
                        const url = err.config.url || '';
                        const isAuthEndpoint = url.includes('/users/me') || url.includes('/users/lookup') || url.includes('/login');
                        if (isAuthEndpoint && sessionStorage.getItem('areWeLocked') !== 'locked') {
                            this.emit('onAutoLogout', 'User credentials are not correct');
                        }

                        if (isAuthEndpoint) {
                            this.setSession(null);
                        }
                    }
                    throw err;
                })
        );
    };

    handleAuthentication = async () => {
        try {
            const session = await getCurrentSession();
            const accessToken = session.getIdToken().getJwtToken();

            if (!accessToken) {
                return;
            }

            if (this.isAuthTokenValid(accessToken)) {
                this.setSession(accessToken);
                this.emit('onAutoLogin', true);
            }
            // eslint-disable-next-line no-empty
        } catch {}
    };

    signInWithCognitoSession = async () => {
        const session = await getCurrentSession();
        this.setSession(session.getIdToken().getJwtToken());
        return this.fetchUserData();
    };

    setSession = (accessToken) => {
        if (accessToken) {
            axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            axios.defaults.headers.customer = getRegionForAPI();
            axios.defaults.crossDomain = true;
            const loginas = sessionStorage.getItem('selected_email');
            const loginasId = sessionStorage.getItem('selected_user');
            if (loginas) {
                axios.defaults.headers.common.loginas = loginas;
                if (loginasId) {
                    axios.defaults.headers.common['loginas-id'] = loginasId;
                } else {
                    delete axios.defaults.headers.common['loginas-id'];
                }
            } else {
                delete axios.defaults.headers.common.loginas;
                delete axios.defaults.headers.common['loginas-id'];
            }
        } else {
            delete axios.defaults.headers.common.Authorization;
            delete axios.defaults.headers.common.loginas;
            delete axios.defaults.headers.common['loginas-id'];
        }
    };

    logout = async () => {
        this.setSession(null);
        const isLocked = sessionStorage.getItem('areWeLocked') === 'locked';
        try {
            if (!isLocked) {
                await signOut();
            }
        } finally {
            sessionStorage.clear();
            window.Intercom('shutdown');
            bootInitialCustomIntercomLauncher();
        }
    };

    lock = async () => {
        const clearUsername = false;
        await signOut(clearUsername);
    };

    isAuthTokenValid = (accessToken) => {
        if (!accessToken) {
            return false;
        }
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;
        return decoded.exp >= currentTime;
    };

    fetchUserData = async () => {
        const response = await axios.get(`${this._getUserHost()}/users/me`);
        const data = resolveId(response.data);
        const { role, subuser } = data;
        if (role === 'admin') {
            sessionStorage.setItem('isAdmin', 'true');
            const impersonationAllowed = data?.permissions?.includes('CanImpersonateUsers');
            sessionStorage.setItem('impersonationAllowed', impersonationAllowed ? 'true' : 'false');
        } else {
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('impersonationAllowed');
        }
        if (role === 'driver' || role === 'receiver') {
            this.setSession(null);
            const error = new Error(`${_.capitalize(role)}s please login through the driver handheld.`);
            error.name = 'RoleError';
            throw error;
        }

        if (subuser) {
            try {
                const parentName = await this.getParentName(data);
                sessionStorage.setItem('user_parent_name', parentName);
            } catch {
                sessionStorage.removeItem('user_parent_name');
            }
        }

        return {
            role,
            data,
        };
    };

    getParentName = async (userData) => {
        const parentId = getId();
        // if not a subuser or is a sub admin return their own name
        if (parentId === userData.id || !userData.subuser || userData.role === 'admin') {
            return userData.name;
        }

        try {
            const { data: parentResponse } = await axios.get(`${this._getUserHost()}/users/${parentId}`);
            const parentData = resolveId(parentResponse);
            return parentData.name;
        } catch {
            return userData.name;
        }
    };

    setMainUser = async (mainUser) => {
        sessionStorage.setItem('main_user_id', mainUser.id);
        const parentName = await this.getParentName(mainUser);
        sessionStorage.setItem('main_user_name', parentName);
    };

    signInAsImpersonatedUser = async (id, mainUser = null) => {
        if (mainUser && !sessionStorage.getItem('main_user_id')) {
            await this.setMainUser(mainUser);
        }
        const response = await axios.get(`${this._getUserHost()}/users/${id}`);
        const data = resolveId(response.data);
        const { email, role } = data;
        axios.defaults.headers.common.loginas = email;
        axios.defaults.headers.common['loginas-id'] = data.id;
        sessionStorage.setItem('isImpersonating', 'true');
        sessionStorage.setItem('selected_user', data.id);
        sessionStorage.setItem('selected_name', data.name);
        sessionStorage.setItem('selected_email', email);
        return { role, data };
    };

    revertImpersonatedLogin = async () => {
        delete axios.defaults.headers.common.loginas;
        delete axios.defaults.headers.common['loginas-id'];
        const response = await axios.get(`${this._getUserHost()}/users/me`);
        const data = resolveId(response.data);
        const { role } = data;
        if (role === 'admin') {
            sessionStorage.setItem('isAdmin', 'true');
            const impersonationAllowed = data?.permissions?.includes('CanImpersonateUsers');
            sessionStorage.setItem('impersonationAllowed', impersonationAllowed ? 'true' : 'false');
        } else {
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('impersonationAllowed');
        }
        sessionStorage.removeItem('isImpersonating');
        sessionStorage.removeItem('selected_user');
        sessionStorage.removeItem('selected_name');
        sessionStorage.removeItem('selected_email');
        sessionStorage.removeItem('main_user_id');
        sessionStorage.removeItem('main_user_name');
        return { role, data };
    };
}

// eslint-disable-next-line new-cap
const instance = new JWTService();

export default instance;
